import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';

import api from '@/services/api';

import Pagination from '@/utils/pagination/Pagination';
import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import UsersSearchFilters from '@/views/pages/Users/elements/UsersSearchFilters';
import UsersContainer from '@/views/pages/Users/elements/UsersContainer';

import ModalDeleteUser from '@/views/pages/Users/modals/ModalDeleteUser';
import ModalUploadUsers from '@/views/pages/Users/modals/ModalUploadUsers';
import ModalAddUser from '@/views/pages/Users/modals/ModalAddUser';
import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import { CheckPermission } from '@/utils/permissions/permissions';

import styles from '@/views/pages/Users/Users.module.scss';

const Users = () => {
	const { dict, isMimAuthAvailable } = useSelector((state) => state.app);
	const { user } = useSelector((state) => state.auth);
	const location = useLocation();
	const navigate = useNavigate();

	const sortMethods = [
		{ value: 'surname', label: dict.sortOptions.surname },
		{ value: '-surname', label: dict.sortOptions.surnameReverse },
	];

	const [ userData, setUserData ] = useState(null);
	const [ pagesNum, setPagesNum ] = useState(null);
	const [ lettersNum, setLettersNum ] = useState(0);

	const [ sortOn, setSortOn ] = useState(sortMethods[0]);
	const [ isLoading, setIsLoading ] = useState(true);

	const [ itemToDelete, setItemToDelete ] = useState(null);
	const [ itemToEdit, setItemToEdit ] = useState(null);
	const [ showModalDeleteItem, setShowModalDeleteItem ] = useState(false);
	const [ showModalUpload, setShowModalUpload ] = useState(false);
	const [ showModalAddDepartment, setShowModalAddDepartment ] = useState(false);

	const { search: locationSearch, pathname: locationPathName } = location;
	
	const changeSort = useCallback((element) => {
		const searchParams = queryString.parse(locationSearch);
		const copiedSearchParams = Object.assign({}, searchParams);
	
		setSortOn(element);
		navigate(`${locationPathName}?${parseObjectToQuery({ ...copiedSearchParams, ord: element.value })}`);
	}, [ setSortOn ]);

	const resetSort = useCallback(() => {
		setSortOn(sortMethods[0]);
	}, [ setSortOn ]);

	useEffect(() => {
		const qu = queryString.parse(location.search);
		const copiedSearchParams = Object.assign({}, qu);
		if (copiedSearchParams.ord) {
			setSortOn(sortMethods.find((el) => { return el.value === copiedSearchParams.ord; }));
		} else {
			setSortOn(sortMethods[0]);
		}
	}, [ location ]);

	const fetchData = ({ data }) => {
		if (data) {
			setUserData(data.users);
			setPagesNum(parseInt(data.pages));
			setLettersNum(data.hits);
		}
	};

	const reloadList = useCallback(() => {
		// if there is no page in location don't fetch data
		const searchParams = Object.fromEntries(new URLSearchParams(location.search));
		if (searchParams.provider) {
			const providerValue = searchParams.provider;
			delete searchParams.provider;
			searchParams.accountType = providerValue;
		}
		setIsLoading(true);
		api.get(`/users?${parseObjectToQuery(searchParams)}`).then(res => {
			fetchData(res);
			setIsLoading(false);
		});
	}, [ location ]);

	useEffect(() => {
		reloadList();
		const qu = Object.fromEntries(new URLSearchParams(location.search));
		if (Object.hasOwn(qu, 'ord')) {
			const foundElement = sortMethods.find(item => item.value === qu.ord);
			if (foundElement) return setSortOn(foundElement);
			setSortOn(sortMethods[0]);
		} else {
			setSortOn(sortMethods[0]);
		}
		// eslint-disable-next-line
    }, [location]);

	const onDeleteItem = (data) => {
		setItemToDelete(data);
	};

	const onEditItem = (data) => {
		setItemToEdit(data);
	};

	return (
		<div className={ styles['users__wrapper'] }>
			<Header narrow={ false } />
			<ModalDeleteUser
				showModalDeleteItem={ showModalDeleteItem }
				setShowModalDeleteItem={ setShowModalDeleteItem }
				itemToDelete={ itemToDelete }
				setItemToDelete={ setItemToDelete }
				reloadList={ reloadList }
			/>
			<ModalUploadUsers 
				reloadList={ reloadList }
				showModalUpload={ showModalUpload }
				setShowModalUpload={ setShowModalUpload }
			/>
			{showModalAddDepartment && <ModalAddUser 
				itemToEdit={ itemToEdit }
				setItemToEdit={ setItemToEdit }
				reloadList={ reloadList }
				showModalAddDepartment={ showModalAddDepartment }
				setShowModalAddDepartment={ setShowModalAddDepartment }
			/>}
			
			<main className={ styles['users__main'] }>
				<h1 className={ styles['users__header'] }>{dict.users.users}
					{lettersNum > 0 && (
						<span className={ styles['users__header--number'] }>&nbsp;({lettersNum})</span>)}
				</h1>
				<div className={ styles['users__description'] }>
					<div className={ styles['users__description--text'] }>
						{isMimAuthAvailable ? dict.users.description : dict.users.descriptionNoMim}
					</div>
					{CheckPermission("canManageUsers", user.role) && <div className={ styles['users__description--buttons'] }>
						<button className='button__blue'
							onClick={ () => setShowModalUpload(true) }
						>
							<i className='icon-upload' />
							{dict.users.buttons.loadFile}
						</button>
						<button
							className='button__blue'
							onClick={ () => setShowModalAddDepartment(true) }
						>+ {dict.users.buttons.addUser}
						</button>
					</div>}
				</div>
				<div className={ styles['users__filters--wrapper'] }>
					<UsersSearchFilters resetSort={ resetSort } />
				</div>
				<Pagination
					pagesNum={ pagesNum }
					sortingOptions={ sortMethods }
					sortingValue={ sortOn }
					sortingOnChange={ changeSort }
				>
					<UsersContainer
						userData={ userData }
						isLoading={ isLoading }
						onEditItem={ onEditItem }
						setShowModalAddDepartment= { setShowModalAddDepartment }
						onDeleteItem={ onDeleteItem }
						setShowModalDeleteItem={ setShowModalDeleteItem }
					/>
				</Pagination>
			</main>

			<Footer narrow={ false } />
		</div>
	);
};
export default React.memo(Users);